/**
 * @friskai/frisk-js Redaction Types
 *
 * Types for data redaction functionality.
 */

/**
 * Redaction option - can be:
 * - `true` to redact all fields
 * - `false` to redact nothing
 * - An array of field names/paths to selectively redact
 */
export type RedactOption = boolean | string[]

/**
 * Result of a dictionary redaction operation
 */
export interface RedactedObject {
  /**
   * The redacted dictionary value
   */
  value: Record<string, unknown>

  /**
   * Paths that were redacted (e.g., ["password", "user.ssn"])
   */
  redactedPaths: string[]
}

/**
 * Result of a serialized object redaction operation
 */
export interface RedactedSerializedObject {
  /**
   * The serialized (JSON string) value, or null if input was null
   */
  value: string | null

  /**
   * Paths that were redacted
   */
  redactedPaths: string[]
}

/**
 * Combine multiple redact options into one
 *
 * @param options - Array of redact options to combine
 * @returns Combined redact option
 */
export function combineRedactOptions(
  ...options: (RedactOption | null | undefined)[]
): RedactOption {
  const paths: string[] = []
  let redactAll = false

  for (const opt of options) {
    if (opt === true) {
      redactAll = true
    } else if (Array.isArray(opt)) {
      paths.push(...opt)
    }
    // false or null/undefined - no change
  }

  if (redactAll) {
    return true
  }

  if (paths.length > 0) {
    // Deduplicate paths
    return [...new Set(paths)]
  }

  return false
}

function deleteDotPath(root: Record<string, unknown>, path: string): boolean {
  const parts = path.split('.').filter((p) => p.length > 0)
  if (parts.length === 0) return false

  let cur: unknown = root
  for (const key of parts.slice(0, -1)) {
    if (cur === null || typeof cur !== 'object' || Array.isArray(cur))
      return false
    if (!(key in (cur as Record<string, unknown>))) return false
    cur = (cur as Record<string, unknown>)[key]
  }

  if (cur === null || typeof cur !== 'object' || Array.isArray(cur))
    return false
  const last = parts[parts.length - 1]
  if (!(last in (cur as Record<string, unknown>))) return false
  delete (cur as Record<string, unknown>)[last]
  return true
}

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * Redact fields from a dictionary based on the redact option.
 *
 * Supports dot-separated paths for nested field removal (e.g. "user.email").
 * Only object traversal is supported — array indices are not interpreted.
 *
 * @param data - The dictionary to redact
 * @param redactOption - The redaction configuration
 * @returns The redacted dictionary with paths that were redacted
 */
export function redactObject(
  data: Record<string, unknown>,
  redactOption?: RedactOption,
): RedactedObject {
  if (redactOption === undefined || redactOption === false) {
    return { value: data, redactedPaths: [] }
  }

  if (redactOption === true) {
    return { value: {}, redactedPaths: ['*'] }
  }

  if (!Array.isArray(redactOption) || redactOption.length === 0) {
    return { value: data, redactedPaths: [] }
  }

  const out = deepClone(data)
  const hits: string[] = []
  const seen = new Set<string>()

  for (const rawPath of redactOption) {
    const path = rawPath.trim()
    if (path.length === 0) continue
    if (deleteDotPath(out, path) && !seen.has(path)) {
      seen.add(path)
      hits.push(path)
    }
  }

  return { value: out, redactedPaths: hits }
}
